import type { HttpToolDefinitionInput } from '../../lib/api';
import { HighlightedTemplate } from './HighlightedTemplate';
import { DEFAULT_CREDENTIAL_SLOT, secretVariable, type ToolAuthConfig } from './tool-inputs';
import styles from './ToolForm.module.css';

interface RequestPreviewProps {
  draft: HttpToolDefinitionInput;
}

interface HeaderLine {
  name: string;
  value: string;
}

/** The header the engine auto-attaches the chosen secret to, shown as its variable. */
function authHeaderLine(draft: HttpToolDefinitionInput): HeaderLine {
  const { credentialSlot, headerName } = (draft.auth_config ?? {}) as ToolAuthConfig;
  const secret = secretVariable(credentialSlot || DEFAULT_CREDENTIAL_SLOT);
  switch (draft.auth_type) {
    case 'bearer':
      return { name: 'Authorization', value: `Bearer ${secret}` };
    case 'basic':
      return { name: 'Authorization', value: `Basic «base64 ${secret}»` };
    default:
      return { name: headerName || 'x-api-key', value: secret };
  }
}

function locationFields(
  mapping: Record<string, unknown> | null | undefined,
  location: 'body' | 'query' | 'headers',
) {
  const block = ((mapping ?? {}) as Record<string, unknown>)[location];
  return block && typeof block === 'object' ? (block as Record<string, string>) : {};
}

export const RequestPreview = ({ draft }: RequestPreviewProps) => {
  const method = draft.http_method.toUpperCase();
  const base = draft.base_url.replace(/\/$/, '');
  const path = draft.path_template || '';
  const query = locationFields(draft.request_mapping, 'query');
  const queryString = Object.entries(query)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
  const url = `${base}${path}${queryString ? `?${queryString}` : ''}`;

  const body = locationFields(draft.request_mapping, 'body');
  const hasBody = method !== 'GET' && method !== 'HEAD' && Object.keys(body).length > 0;

  const mappingHeaders = Object.entries(locationFields(draft.request_mapping, 'headers')).map(
    ([name, value]) => ({ name, value }),
  );
  const headers: HeaderLine[] = [authHeaderLine(draft), ...mappingHeaders];
  if (hasBody) headers.push({ name: 'Content-Type', value: 'application/json' });

  if (!base) {
    return <p className={styles.previewEmpty}>Fill in the steps above to see the request we’ll send.</p>;
  }

  return (
    <pre className={styles.preview}>
      <code>
        <span className={styles.previewMethod}>{method}</span> <HighlightedTemplate text={url} />
        {headers.map(header => (
          <span key={header.name} className={styles.previewHeader}>
            {'\n'}
            {header.name}: <HighlightedTemplate text={header.value} />
          </span>
        ))}
        {hasBody && (
          <>
            {'\n\n'}
            <HighlightedTemplate text={JSON.stringify(body, null, 2)} />
          </>
        )}
      </code>
    </pre>
  );
};
